/**
 * Admin endpoints for the brokers catalog + state changes + audit history.
 *
 * Backed by:
 *   GET    /admin/brokers
 *   POST   /admin/brokers/{id}/status
 *   GET    /admin/brokers/{id}/activity
 */

import { z } from 'zod';

import type { ApiResult } from './client';
import { get, post } from './client';

/* ============================================================================
 * Schemas
 * ============================================================================ */

export const brokerCatalogStatusSchema = z.enum(['active', 'maintenance', 'closed']);

export const brokerCatalogSchema = z.object({
  id: z.string(),
  // Driver-related: each commercial broker FKs to broker_drivers.
  driver_id: z.string(),
  driver_display_name: z.string().nullable(),
  // Commercial entity fields:
  display_name: z.string(),
  legal_name: z.string().nullable(),
  regulator: z.string().nullable(),
  regulator_id: z.string().nullable(),
  country_id: z.string().nullable(),
  default_base_currency: z.string(),
  status: brokerCatalogStatusSchema,
  api_base_urls: z.record(z.unknown()),
  rate_limit_per_sec: z.number(),
  support_email: z.string().nullable(),
  support_phone: z.string().nullable(),
  notes: z.string().nullable(),
  maintenance_until: z.string().nullable(),
  status_changed_at: z.string().nullable(),
  status_changed_by: z.string().nullable(),
  status_changed_by_email: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const brokerCatalogListSchema = z.array(brokerCatalogSchema);

export const auditEventSchema = z.object({
  id: z.string(),
  operator_id: z.string(),
  operator_email: z.string().nullable(),
  operator_display_name: z.string().nullable(),
  target_kind: z.string(),
  target_id: z.string(),
  action: z.string(),
  before_state: z.record(z.unknown()).nullable(),
  after_state: z.record(z.unknown()).nullable(),
  reason: z.string(),
  request_id: z.string().nullable(),
  at: z.string(),
});

export const auditEventListSchema = z.array(auditEventSchema);

export type BrokerCatalogRow = z.infer<typeof brokerCatalogSchema>;
export type BrokerCatalogStatus = z.infer<typeof brokerCatalogStatusSchema>;
export type AuditEvent = z.infer<typeof auditEventSchema>;

/* ============================================================================
 * Endpoints
 * ============================================================================ */

export const listBrokersCatalog = (
  signal?: AbortSignal,
): Promise<ApiResult<BrokerCatalogRow[]>> =>
  get('/admin/brokers', brokerCatalogListSchema, { signal });

// ─── Driver credential spec (drives the dynamic onboarding form) ─────────────
export const credentialFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.string(),                       // string | integer | boolean
  required: z.boolean(),
  secret: z.boolean(),                    // render masked, never pre-filled
  description: z.string().nullable().optional(),
  default: z.unknown().nullable().optional(),
});
export const credentialSpecSchema = z.object({
  driver_id: z.string(),
  auth_method: z.string(),
  fields: z.array(credentialFieldSchema),
});
export type CredentialField = z.infer<typeof credentialFieldSchema>;
export type CredentialSpec = z.infer<typeof credentialSpecSchema>;

export const getDriverCredentialSpec = (
  driverId: string,
  signal?: AbortSignal,
): Promise<ApiResult<CredentialSpec>> =>
  get(
    `/admin/broker-drivers/${encodeURIComponent(driverId)}/credential-spec`,
    credentialSpecSchema,
    { signal },
  ) as Promise<ApiResult<CredentialSpec>>;

export interface ChangeBrokerStatusRequest {
  status: BrokerCatalogStatus;
  maintenance_until?: string | null;
  reason: string;
}

export const changeBrokerStatus = (
  broker_id: string,
  body: ChangeBrokerStatusRequest,
  signal?: AbortSignal,
): Promise<ApiResult<BrokerCatalogRow>> =>
  post(
    `/admin/brokers/${encodeURIComponent(broker_id)}/status`,
    brokerCatalogSchema,
    body,
    { signal },
  );

export const getBrokerActivity = (
  broker_id: string,
  signal?: AbortSignal,
): Promise<ApiResult<AuditEvent[]>> =>
  get(
    `/admin/brokers/${encodeURIComponent(broker_id)}/activity`,
    auditEventListSchema,
    { signal },
  );
